import { type FC, useCallback, useLayoutEffect, useMemo, useRef } from "react";

import { Alert, Badge, Button, Card, Group, Loader, SegmentedControl, Skeleton, Stack, Text } from "@mantine/core";
import { Tab } from "@snapchat/snap-design-system";
import { clsx } from "clsx";
import { useIntl } from "react-intl";

import { LensPayoutState, LensScoreFinalizationStatus } from "../../../gql-generated/types";
import {
    AllTime,
    LensPlusScore,
    LensPlusScoreTooltip,
    LensPlusSubscriptionsEarned,
    LensPlusSubscriptionsEarnedTooltip,
    MonthPayout,
    PayoutDataError,
    TotalLensPlusAudience,
    TotalLensPlusAudienceTooltip,
} from "../../../lens-creator-payout/components/messages.generated";
import { formatMilliUsd } from "../../../lens-creator-payout/utils/formatMilliUsd";
import { InsightsSubTabs } from "../../../lens-insights/components/InsightsSubTabs";
import { SubTabContent } from "../../../lens-insights/components/SubTabContent";
import { SubTabHeader } from "../../../lens-insights/components/SubTabHeader";
import { BarChartComponent } from "../../../lens-insights/components/charts/BarChartComponent";
import {
    ClearSelection,
    ShowingSelectedLens,
} from "../../../lens-insights/components/payout-monetization-tab/messages.generated";
import { MonthlyPayoutsChart } from "../../../lens-insights/components/payout-monetization-tab/payout-history/MonthlyPayoutsChart";
import { useLensIdSearchParameter } from "../../../lens-insights/hooks/useLensIdSearchParameter";
import { formatNullableNumber } from "../../../lens-stats/formatters/formatNumbers";
import { StatsCard } from "../../../shared/components/StatsCard";
import { PayoutTosBanner } from "../../../shared/components/tos";
import { useLensPlusActivityTotals } from "../../hooks/useLensPlusActivityTotals";
import { type useLensPlusMonth } from "../../hooks/useLensPlusMonth";
import { useLensPlusMonthlyScores } from "../../hooks/useLensPlusMonthlyScores";
import { type LensPlusPayoutHistory } from "../../hooks/useLensPlusPayoutHistory";
import { lensPlusComparison } from "../../utils/lensPlusComparison";
import { lensPlusDailyMetrics } from "../../utils/lensPlusDailyMetrics";
import { lensSummary, metricNote as lensSummaryNote } from "../LensSummaryGrid.module.css";
import * as bandStyles from "../top-performer/HowPayoutsWorkBand.module.css";
import { chip } from "../top-performer/TopPerformerStatusChip.module.css";
import { card } from "../top-performer/TopPerformerTab.module.css";
import { HowPayoutsWork, UpdatedDaily } from "../top-performer/messages.generated";

import { formatLensPlusSignups } from "./LensPlusLensColumns";
import { LensPlusLensTable } from "./LensPlusLensTable";
import styles from "./LensPlusTab.module.css";
import { PayoutCycleCard } from "./PayoutCycleCard";
import {
    DailyBreakdown,
    Estimated,
    LensesTitle,
    NoDailyData,
    PaidEarningsTooltip,
    SignupPrivacy,
    StatusLabel,
    messages,
} from "./messages.generated";

const CARD_PROPS = { size: "normal", diffValue: null, diffText: null } as const;
const CHIP_PROPS = { radius: "xl", className: chip, variant: "light", tt: "none" } as const;
const METRICS = [
    { key: "subscriberActionsUu", label: <TotalLensPlusAudience />, tooltip: <TotalLensPlusAudienceTooltip /> },
    { key: "subscribeCount", label: <LensPlusSubscriptionsEarned />, tooltip: <LensPlusSubscriptionsEarnedTooltip /> },
    { key: "lensScore", label: <LensPlusScore />, tooltip: <LensPlusScoreTooltip /> },
] as const;

export const LensPlusTab: FC<{
    selection: ReturnType<typeof useLensPlusMonth>;
    history: LensPlusPayoutHistory;
}> = ({ selection, history }) => {
    const { month, setMonth: onMonthChange, view, setView: onViewChange } = selection;
    const historyCard = useRef<HTMLDivElement>(null);
    const historyPosition = useRef<number | null>(null);
    const allTime = view === "all-time";
    const dayView = view === "day";
    const selectMonth = useCallback(
        (value: string) => {
            historyPosition.current = historyCard.current?.getBoundingClientRect().top ?? null;
            onMonthChange(value, "day");
        },
        [onMonthChange],
    );
    const intl = useIntl();
    const chartPeriods = useMemo(
        () =>
            history.points.map((point) => ({
                period: `${point.month}-01T00:00:00Z`,
                totalAmountMilliUsd: point.amountMilliUsd,
            })),
        [history.points],
    );
    const { lensId: selectedLensId } = useLensIdSearchParameter();
    const creator = useLensPlusMonthlyScores(month, allTime, {
        ignoreSelection: true,
        summaryOnly: true,
        includeDailyBreakdown: true,
    });
    const lensList = useLensPlusMonthlyScores(month, allTime, { ignoreSelection: true, listOnly: true });
    const selected = useLensPlusMonthlyScores(month, allTime || !selectedLensId, {
        summaryOnly: true,
        includeDailyBreakdown: true,
    });
    const activity = useLensPlusActivityTotals(!allTime, true);
    const selectedActivity = useLensPlusActivityTotals(!allTime || !selectedLensId);
    const summary = selectedLensId ? selectedActivity : activity;
    const {
        window,
        estimatedTotals,
        missingTotalsCount,
        paidAmountMilliUsd,
        loading,
        hasError,
        hasRefreshError,
        refetch,
        payoutMinimumScore: minimum,
        submittedCount,
        scoredCount,
        availableScoredCount,
    } = selectedLensId ? selected : creator;
    const { lensId, lensName, clearLensId } = allTime ? summary : selectedLensId ? selected : creator;
    useLayoutEffect(() => {
        if (historyPosition.current === null || !historyCard.current) return;
        const offset = historyCard.current.getBoundingClientRect().top - historyPosition.current;
        if (offset) globalThis.scrollBy({ top: offset, behavior: "instant" });
        if (!loading) historyPosition.current = null;
    }, [month, view, loading]);
    const current = month === new Date().toISOString().slice(0, 7);
    const totals = window?.totals;
    const availableTotals = current || Boolean(window) || (submittedCount ?? 0) > 0 ? estimatedTotals : null;
    const displayTotals = allTime ? summary.totals : totals ?? availableTotals;
    const displayScoredCount = scoredCount ?? availableScoredCount;
    const partialScoredCount = scoredCount === null && availableScoredCount !== null && missingTotalsCount > 0;
    const summaryLoading = allTime ? summary.loading : loading;
    const totalsPending = !allTime && summaryLoading;
    const metricValue = (key: (typeof METRICS)[number]["key"]) =>
        key === "subscribeCount"
            ? formatLensPlusSignups(displayTotals?.[key])
            : formatNullableNumber(displayTotals?.[key]);
    const start = new Date(`${month}-01T00:00:00Z`);
    const monthLabel = intl.formatDate(start, { month: "long", timeZone: "UTC" });
    const previousMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - 1, 1))
        .toISOString()
        .slice(0, 7);
    const previous = useLensPlusMonthlyScores(
        previousMonth,
        allTime || loading || hasError || previousMonth < "2000-01",
        { summaryOnly: true },
    );
    const comparison = (value: number | null | undefined, prior: number | null | undefined, signups = false) => ({
        diffValue:
            allTime || Boolean(estimatedTotals) || summaryLoading || previous.loading || previous.hasError
                ? null
                : lensPlusComparison(value, prior, signups),
        diffText: intl.formatMessage(messages.comparedWithMonth, {
            month: intl.formatDate(new Date(`${previousMonth}-01T00:00:00Z`), { month: "long", timeZone: "UTC" }),
            partial: String(current),
        }),
    });
    const closeDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
    const nextMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
    const date = (value: Date | string) => intl.formatDate(value, { month: "short", day: "numeric", timeZone: "UTC" });
    const pending = window?.payout.state === LensPayoutState.Pending || window?.payout.state === LensPayoutState.NotDue;
    let payoutStatus = current ? "awaiting" : undefined;
    if (pending) payoutStatus = "pending";
    else if (window?.payout.state === LensPayoutState.Paid) payoutStatus = "paid";
    else if (window) payoutStatus = "noPayout";
    let payoutValue = "-";
    if (paidAmountMilliUsd > 0 || window?.payout.state === LensPayoutState.Paid)
        payoutValue = formatMilliUsd(paidAmountMilliUsd);
    const scoreBadge = allTime
        ? intl.formatMessage(messages.lifetime)
        : current
          ? intl.formatMessage(messages.scoreReset, { date: date(nextMonth) })
          : window?.finalizationStatus === LensScoreFinalizationStatus.Estimated && displayTotals
            ? intl.formatMessage(messages.projected)
            : window?.finalizationStatus === LensScoreFinalizationStatus.Finalized && totals
              ? intl.formatMessage(messages.scoreFinal)
              : undefined;
    const historicalOnly =
        !current && window?.payout.state === LensPayoutState.Paid && (!totals || window.dailyBuckets.length === 0);
    const header = (
        <>
            <PayoutTosBanner />
            <Text className="paragraph-p300" c="dimmed">
                {intl.formatMessage(messages.calendarNote)}
            </Text>
            {lensId && (
                <Alert>
                    <ShowingSelectedLens lensName={<strong>{lensName ?? history.lensName ?? lensId}</strong>} />
                    <Button variant="subtle" size="compact-xs" onClick={clearLensId}>
                        <ClearSelection />
                    </Button>
                </Alert>
            )}
        </>
    );
    return (
        <Stack gap="lg" pt="lg">
            {header}
            {!allTime && (hasError || hasRefreshError) && (
                <Alert color="red">
                    {hasRefreshError ? intl.formatMessage(messages.refreshFailed) : <PayoutDataError />}
                    <Button variant="subtle" onClick={refetch}>
                        {intl.formatMessage(messages.retry)}
                    </Button>
                </Alert>
            )}
            {!allTime && !hasError && (
                <div className={styles.cycleContainer} aria-busy={loading}>
                    <Skeleton pos="absolute" inset={0} hidden={!loading} visible={loading} />
                    <PayoutCycleCard
                        month={month}
                        window={window}
                        paidAmountMilliUsd={paidAmountMilliUsd}
                        lastPayout={history.lastPayout}
                    />
                </div>
            )}
            {!allTime &&
                (current || Boolean(totals)) &&
                window?.finalizationStatus === LensScoreFinalizationStatus.Estimated && (
                    <Text className="paragraph-p300" c="dimmed" fw={700}>
                        <Estimated />
                    </Text>
                )}
            <Card className={card} hidden={!allTime && hasError}>
                <Group gap="md">
                    <h3 className={clsx("heading-h400", styles.heading)}>
                        {allTime ? (
                            <AllTime />
                        ) : (
                            intl.formatMessage(messages.summaryTitle, {
                                month: monthLabel,
                                current: String(current),
                            })
                        )}
                    </h3>
                    {totalsPending && (
                        <Group gap="xs" wrap="nowrap" role="status">
                            <Loader size="xs" color="var(--colors-black-v50)" aria-hidden="true" />
                            <Text className={clsx("paragraph-p300", styles.loadingNote)}>
                                {intl.formatMessage(messages.loadingTotals)}
                            </Text>
                        </Group>
                    )}
                </Group>
                <Text className="paragraph-p300">
                    {intl.formatMessage(
                        allTime
                            ? messages.allTimeNote
                            : !current && !displayTotals && !totalsPending
                              ? messages.historicalMetricsUnavailable
                              : messages.summaryNote,
                    )}
                </Text>
                {!allTime && availableTotals && (
                    <Text className="paragraph-p300" role="status">
                        {intl.formatMessage(
                            current || window?.finalizationStatus === LensScoreFinalizationStatus.Estimated
                                ? messages.partialTotals
                                : messages.availableTotalsNote,
                            { count: missingTotalsCount },
                        )}
                    </Text>
                )}
                {!allTime && !summaryLoading && !displayTotals && missingTotalsCount > 0 && (
                    <Text className="paragraph-p300" role="status">
                        {intl.formatMessage(messages.unavailableTotals, { count: missingTotalsCount })}
                    </Text>
                )}
                {allTime && (summary.hasError || summary.hasRefreshError) && (
                    <Alert color="red">
                        {summary.paidAmountMilliUsd === undefined ? (
                            <PayoutDataError />
                        ) : (
                            intl.formatMessage(messages.refreshFailed)
                        )}
                        <Button variant="subtle" onClick={summary.refetch}>
                            {intl.formatMessage(messages.retry)}
                        </Button>
                    </Alert>
                )}
                <div className={styles.summary}>
                    {METRICS.map(({ key, label, tooltip }) => (
                        <div key={key} className={styles.metric}>
                            <StatsCard
                                {...CARD_PROPS}
                                label={
                                    <>
                                        {label}
                                        {key === "lensScore" && scoreBadge && (
                                            <Badge {...CHIP_PROPS} color="gray">
                                                {scoreBadge}
                                            </Badge>
                                        )}
                                    </>
                                }
                                tooltip={
                                    <>
                                        {allTime
                                            ? intl.formatMessage(messages.lifetimeMetricNote, { kind: key })
                                            : tooltip}
                                        {key === "subscribeCount" && <SignupPrivacy />}
                                    </>
                                }
                                reserveDiffSpace={!allTime}
                                {...comparison(
                                    displayTotals?.[key],
                                    previous.window?.totals?.[key],
                                    key === "subscribeCount",
                                )}
                                loading={summaryLoading}
                                value={metricValue(key)}
                            />
                            <div className={clsx("paragraph-p300", styles.metricNote)}>
                                {allTime ? intl.formatMessage(messages.lifetimeMetricNote, { kind: key }) : tooltip}
                            </div>
                        </div>
                    ))}
                    <div
                        className={clsx(
                            styles.metric,
                            allTime || window?.payout.state === LensPayoutState.Paid
                                ? styles.paidPayout
                                : styles.payout,
                        )}
                    >
                        <StatsCard
                            {...CARD_PROPS}
                            label={
                                <>
                                    {allTime ? (
                                        intl.formatMessage(messages.totalPaid)
                                    ) : (
                                        <>
                                            <MonthPayout month={monthLabel} />{" "}
                                            {payoutStatus && (
                                                <Badge {...CHIP_PROPS} color={pending ? "orange" : "gray"}>
                                                    <StatusLabel status={payoutStatus} />
                                                </Badge>
                                            )}
                                        </>
                                    )}
                                </>
                            }
                            reserveDiffSpace={!allTime}
                            {...comparison(
                                window?.payout.state === LensPayoutState.Paid ? paidAmountMilliUsd : undefined,
                                previous.window?.payout.state === LensPayoutState.Paid
                                    ? previous.paidAmountMilliUsd
                                    : undefined,
                            )}
                            tooltip={allTime ? intl.formatMessage(messages.totalPaidNote) : <PaidEarningsTooltip />}
                            loading={summaryLoading}
                            value={
                                summaryLoading
                                    ? "-"
                                    : allTime
                                      ? summary.paidAmountMilliUsd === undefined
                                          ? "-"
                                          : formatMilliUsd(summary.paidAmountMilliUsd)
                                      : payoutValue
                            }
                        />
                        <div className={clsx("paragraph-p300", styles.metricNote)}>
                            {allTime
                                ? intl.formatMessage(messages.totalPaidNote)
                                : window?.payout.state === LensPayoutState.Paid && window.payout.paidOn
                                  ? intl.formatMessage(messages.paidOn, { date: date(window.payout.paidOn) })
                                  : intl.formatMessage(messages.payoutTiming, { closeDate: date(closeDate) })}
                        </div>
                    </div>
                </div>
            </Card>
            <section id="lens-plus-payout-steps" className={clsx(bandStyles.band, styles.band)}>
                <h2 className={clsx("heading-h400", styles.bandHeading)}>
                    <HowPayoutsWork />
                </h2>
                <ol className={bandStyles.steps}>
                    {["users", "score", "paid"].map((step, index) => (
                        <li key={step} className={bandStyles.step}>
                            <span className={clsx("heading-h700", bandStyles.stepNumber)} aria-hidden="true">
                                {index + 1}
                            </span>
                            <div>
                                <div className={clsx("heading-h600", bandStyles.stepTitle)}>
                                    {intl.formatMessage(messages.stepTitle, { step })}
                                </div>
                                <div className={clsx("paragraph-p300", bandStyles.stepBody)}>
                                    {intl.formatMessage(messages.stepBody, { step })}
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            </section>
            <Card className={card} ref={historyCard}>
                <Group justify="space-between">
                    <h3 className="heading-h400">{intl.formatMessage(messages.historyTitle)}</h3>
                    <SegmentedControl
                        value={dayView ? "day" : "monthly"}
                        radius="xl"
                        withItemsBorders={false}
                        data={[
                            { value: "monthly", label: intl.formatMessage(messages.historyMonthly) },
                            { value: "day", label: <DailyBreakdown /> },
                        ]}
                        onChange={(mode) => {
                            if (mode === "day" || (!allTime && mode === "monthly")) onViewChange(mode);
                        }}
                    />
                </Group>
                {dayView ? (
                    <InsightsSubTabs>
                        {METRICS.map(({ key, label }) => (
                            <Tab
                                key={key}
                                title={
                                    <SubTabHeader label={label} statValue={metricValue(key)} loading={summaryLoading} />
                                }
                            >
                                <SubTabContent className={styles.chartContent}>
                                    {hasError ? null : loading ? (
                                        <Skeleton h={250} />
                                    ) : (window?.dailyBuckets.length ?? 0) > 0 ? (
                                        <>
                                            <BarChartComponent
                                                responsive
                                                scrollable
                                                latestBarPending={false}
                                                data={lensPlusDailyMetrics(
                                                    window ? { ...window, totals: displayTotals ?? null } : null,
                                                    key,
                                                )}
                                                contentName={label}
                                                formatter={(value) =>
                                                    key === "subscribeCount"
                                                        ? formatLensPlusSignups(Number(value))
                                                        : formatNullableNumber(Number(value))
                                                }
                                                minimumVisibleValue={key === "subscribeCount" ? 10 : undefined}
                                            />
                                            {key === "subscriberActionsUu" && (
                                                <Text className="paragraph-p300">
                                                    {intl.formatMessage(messages.dailyUsersNote)}
                                                </Text>
                                            )}
                                            {key === "subscribeCount" && (
                                                <Text className="paragraph-p300" c="dimmed">
                                                    {intl.formatMessage(messages.signupChartPrivacy)}
                                                </Text>
                                            )}
                                        </>
                                    ) : (
                                        <Text className="paragraph-p300">
                                            <NoDailyData />
                                        </Text>
                                    )}
                                </SubTabContent>
                            </Tab>
                        ))}
                    </InsightsSubTabs>
                ) : (
                    <SubTabContent className={styles.chartContent}>
                        {(history.hasError || history.hasRefreshError) && (
                            <Alert color="red">
                                {history.hasRefreshError ? (
                                    intl.formatMessage(messages.refreshFailed)
                                ) : (
                                    <PayoutDataError />
                                )}
                                <Button variant="subtle" onClick={history.refetch}>
                                    {intl.formatMessage(messages.retry)}
                                </Button>
                            </Alert>
                        )}
                        {history.hasError ? null : history.loading ? (
                            <Skeleton h={250} />
                        ) : (
                            <MonthlyPayoutsChart
                                showHeading={false}
                                scrollable
                                selectedMonth={allTime ? undefined : month}
                                onMonthSelect={selectMonth}
                                periods={chartPeriods}
                            />
                        )}
                        {allTime && history.hasMoreError && (
                            <Alert color="red">
                                <PayoutDataError />
                            </Alert>
                        )}
                        {allTime && history.hasMore && (
                            <Button variant="subtle" loading={history.loadingMore} onClick={history.fetchMore}>
                                {intl.formatMessage(messages.loadOlder)}
                            </Button>
                        )}
                    </SubTabContent>
                )}
            </Card>
            <Card className={card}>
                <Group justify="space-between">
                    <h3 className="heading-h400">
                        <LensesTitle month={allTime ? intl.formatMessage(messages.allTimeLenses) : monthLabel} />
                    </h3>
                    {(allTime || current) && (
                        <Text className="paragraph-p300" c="dimmed">
                            <UpdatedDaily />
                        </Text>
                    )}
                </Group>
                {!allTime && !lensId && window && (
                    <Text className="paragraph-p300" c="dimmed">
                        {intl.formatMessage(
                            historicalOnly ? messages.historicalLensesSubtitle : messages.lensesSubtitle,
                        )}
                    </Text>
                )}
                {!allTime && !lensId && window && !historicalOnly && (
                    <div className={lensSummary}>
                        {[
                            {
                                key: "submitted",
                                label: intl.formatMessage(messages.lensesSubmitted),
                                value: formatNullableNumber(submittedCount),
                            },
                            {
                                key: "scored",
                                label: intl.formatMessage(messages.lensesScored, { minimum }),
                                value: formatNullableNumber(displayScoredCount),
                            },
                            {
                                key: "hitRate",
                                label: intl.formatMessage(messages.lensesHitRate),
                                value:
                                    submittedCount === null || displayScoredCount === null
                                        ? "-"
                                        : intl.formatNumber(submittedCount ? displayScoredCount / submittedCount : 0, {
                                              style: "percent",
                                              maximumFractionDigits: 1,
                                          }),
                            },
                        ].map(({ key, label, value }) => (
                            <div key={key} className={key === "hitRate" ? styles.payout : undefined}>
                                <StatsCard
                                    {...CARD_PROPS}
                                    label={
                                        <>
                                            {label}
                                            {key === "scored" &&
                                                (current || Boolean(totals)) &&
                                                window.finalizationStatus === LensScoreFinalizationStatus.Estimated && (
                                                    <Badge {...CHIP_PROPS} color="orange">
                                                        {intl.formatMessage(messages.projected)}
                                                    </Badge>
                                                )}
                                        </>
                                    }
                                    tooltip={intl.formatMessage(messages.lensesSummaryNote, {
                                        kind: key,
                                        month: monthLabel,
                                        minimum,
                                    })}
                                    value={value}
                                />
                                <div className={clsx("paragraph-p300", lensSummaryNote)}>
                                    {intl.formatMessage(messages.lensesSummaryNote, {
                                        kind: key,
                                        month: monthLabel,
                                        minimum,
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {!allTime && !lensId && window && !historicalOnly && partialScoredCount && (
                    <Text className="paragraph-p300" c="dimmed" role="status">
                        {intl.formatMessage(messages.availableScoredNote, { count: missingTotalsCount })}
                    </Text>
                )}
                <LensPlusLensTable
                    month={month}
                    allTime={allTime}
                    minimum={minimum}
                    monthly={lensList}
                    activity={activity}
                />
            </Card>
        </Stack>
    );
};
